import React, { useContext, useState, useEffect, useCallback } from "react";
import Card from "../UI/Card";
import AuthContext from "../../store/auth-context";
import AppContext from "../../store/app-context";
import { useQuery, useMutation } from "@apollo/client";
import { B3_GET_COMPANY } from "../../graphql/queries/getCompany";
import { B3_GET_ALL_ADDRESSES } from "../../graphql/queries/getAllAddresses";
import { GET_DASHBOARD_COMPANIES } from "../../graphql/queries/getDashboardCompanies";
import { SUPER_ADMIN_MASQUERADING } from "../../graphql/queries/getSuperAdminMasquerading";
import { SUPER_ADMIN_BEGIN_MASQUERADE } from "../../graphql/mutations/superAdminBeginMasquerade";
import ShipToAccounts from "./ShipToAccounts";
import Button from "../UI/Button";
import { parseCookies } from "nookies";

import styles from "./Account.module.css";

const Account = (props) => {
  // get contexts
  const authCtx = useContext(AuthContext);
  const appCtx = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const [isShipAccountModalDisplayed, setIsShipAccountModalDisplayed] =
    useState(false);
  const [selectedShipAddress, setSelectedShipAddress] = useState();

  const showShipAccountModalHandler = () => {
    setIsShipAccountModalDisplayed(true);
  };

  const hideShipAccountModalHandler = () => {
    setIsShipAccountModalDisplayed(false);
  };

  // ***********************            GraphQL Start            *********************************
  {
    /*
    GraphQL Call rules
    1. No calls are made for anon and partially registered users.
    2. For user's with account (1 or more), first call is bundle authorization (see auth-context.js). A B3GraphQL call is made (bundleAuthorization) - This returns user data including role. Role=3 = superadmin
    3. For user with one account (Role != 3, (role is in bundle auth response)) i.e. user is not superAdmin
      	a. calls b3graphQL operation = B3_GET_COMPANY (getCompany) , input = userId from #2, returns soldto info
	      b. call b3graphQL operation = B3_GET_ALL_ADDRESSES (getAllAddresses) , input = companyid from #3a
    4. If the user IS a superadmin then code
        a. calls b3graphql operation = SUPER_ADMIN_MASQUERADING (getSuperAdminMasquerading) , input=entityId from #2
        b. calls b3graphql operation = B3_GET_ALL_ADDRESSES (getAllAddresses) , input = companyId returned from #4a
        c. calls b3graphql operation = GET_DASHBOARD_COMPANIES (getSuperAdminCompanies) to get all values for account dropdown
        d. If the superadmin switches accounts then call SUPER_ADMIN_BEGIN_MASQUERADE (getSuperAdminBeginMasquerade) and repeats steps #4a and #4b
  */
  }
  const COMPANY_SAP_FIELD = "Global SAP Account #";

  // method to get full sap account number from getCompany GraphQL call
  const getCompanySap = (company) => {
    if (company && company.extraFields && company.extraFields.length) {
      const sapField = company.extraFields.find(
        (e) => e.fieldName === COMPANY_SAP_FIELD
      );
      return sapField?.fieldValue ?? "";
    }
    return "";
  };

  // method to get short sap account number from getCompany GraphQL call
  const getCompanySapShortNumber = (company) => {
    const sapNumber = getCompanySap(company);
    const sapArr = sapNumber.split("-");
    return sapArr[sapArr.length - 1];
  };

  const getCompanySalesOrg = (company) => {
    const sapNumber = getCompanySap(company);
    const sapArr = sapNumber.split("-");
    return sapArr[0];
  };

  // method to get company name from superAdminMasquerading GraphQL call
  const getCompanyName = (companyNameString) => {
    if (companyNameString) {
      const companyNameArr = companyNameString.split("|");
      return companyNameArr[companyNameArr.length - 1];
    }
  };

  // make graphql call for user addresses.
  //bundle authorization
  const exsistCookies = parseCookies();

  //b3 userRole. Role=3 -> super admin i.e. user with multiple accounts.
  const userRole = authCtx?.bundleAuth?.authorization?.result?.user?.role;

  //Check if it is punchout session
  const isPunchOutSession = authCtx?.isLoggedIn && authCtx?.sessionUser?.b2bUser;

  console.log("isPunchOutSession:" + isPunchOutSession);

  const superAdminMasqueradingVariables =
    (userRole != undefined || userRole != null) &&
    userRole === 3 &&
    authCtx.bundleAuth.authorization.result.user.entityId
      ? {
          customerId: authCtx.bundleAuth.authorization.result.user.entityId,
        }
      : null;

  const superAdminMasqueradingSkip =
    !authCtx.isLoggedIn ||
    !authCtx.bundleAuth ||
    !authCtx.b3AuthToken ||
    !superAdminMasqueradingVariables ||
    isPunchOutSession ||
    (props.quote && props.quote.quoteStatus !== "Not Submitted");

  const {
    data: superAdminMasqueradingData,
    loading: superAdminMasqueradingLoading,
    error: superAdminMasqueradingError,
    refetch: superAdminMasqueradingRefetch,
  } = useQuery(SUPER_ADMIN_MASQUERADING, {
    variables: superAdminMasqueradingVariables,
    skip: superAdminMasqueradingSkip,
  });

  const superAdminCompaniesVariables =
    !superAdminMasqueradingSkip &&
    authCtx.bundleAuth.authorization.result.user.id
      ? {
          superAdminId: parseInt(
            authCtx.bundleAuth.authorization.result.user.id
          ),
        }
      : null;

  const {
    data: superAdminCompaniesData,
    loading: superAdminCompaniesLoading,
    error: superAdminCompaniesError,
  } = useQuery(GET_DASHBOARD_COMPANIES, {
    variables: superAdminCompaniesVariables,
    skip: superAdminMasqueradingSkip,
  });

  const { edges: superAdminCompanyEdges, totalCount: totalCompanies } =
    superAdminCompaniesData?.superAdminCompanies ?? {
      superAdminCompanyEdges: [],
      totalCompanies: 0,
    };

  const superAdminMsqueradingCompany =
    superAdminMasqueradingData?.superAdminMasquerading?.id;

  const adminCompanies =
    superAdminCompanyEdges &&
    superAdminCompanyEdges.map((item) => (
      <option
        value={item.node.companyId}
        selected={item.node.companyId + "" === superAdminMsqueradingCompany}
      >
        {getCompanySapShortNumber(item.node)}
      </option>
    ));

  const companyVariables =
    (userRole != undefined || userRole != null) &&
    userRole != 3 &&
    authCtx.bundleAuth.authorization.result.user.id
      ? { userId: parseInt(authCtx.bundleAuth.authorization.result.user.id) }
      : null;

  const companySkip =
    !authCtx.isLoggedIn ||
    !authCtx.bundleAuth ||
    !authCtx.b3AuthToken ||
    !companyVariables ||
    isPunchOutSession ||
    (props.quote && props.quote.quoteStatus !== "Not Submitted");

  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
  } = useQuery(B3_GET_COMPANY, {
    variables: companyVariables,
    skip: companySkip,
  });

  const acctNumber =
    superAdminMasqueradingData &&
    superAdminMasqueradingData.superAdminMasquerading
      ? getCompanySapShortNumber(
          superAdminMasqueradingData.superAdminMasquerading
        )
      : companyData && companyData.userCompany
      ? getCompanySapShortNumber(companyData.userCompany)
      : isPunchOutSession && (authCtx?.sessionUser?.b2bUserStatus?.acctNumber)
      ? authCtx?.sessionUser?.b2bUserStatus?.acctNumber
      : "";
      const accountEnabled = appCtx.envProp.react_app_account_enabled && appCtx.envProp.react_app_account_enabled === "true";
  console.log("account number: " + acctNumber);

  const salesOrg = superAdminMasqueradingData &&
  superAdminMasqueradingData.superAdminMasquerading
    ? getCompanySalesOrg(
        superAdminMasqueradingData.superAdminMasquerading
      )
    : companyData && companyData.userCompany
    ? getCompanySalesOrg(companyData.userCompany)
    : "";

  const fullSAPAccountNumber = superAdminMasqueradingData &&
  superAdminMasqueradingData.superAdminMasquerading
    ? getCompanySap(
        superAdminMasqueradingData.superAdminMasquerading
      )
    : companyData && companyData.userCompany
    ? getCompanySap(companyData.userCompany)
    : "";

  const companyName = superAdminMasqueradingData &&
    superAdminMasqueradingData.superAdminMasquerading &&
    superAdminMasqueradingData.superAdminMasquerading.companyName
      ? getCompanyName(
          superAdminMasqueradingData.superAdminMasquerading.companyName
        )
      : companyData?.userCompany?.companyName ?? "-";

  const companyIdString =
    superAdminMsqueradingCompany ?? companyData?.userCompany?.id;
  const addressSkip = !authCtx.isLoggedIn || !companyIdString;
  const companyId = companyIdString ? parseInt(companyIdString) : null;

  const addressVariables = { companyId: companyId };

  const {
    data: addressData,
    loading: addressLoading,
    error: addressError,
    refetch: addressRefetch,
  } = useQuery(B3_GET_ALL_ADDRESSES, {
    variables: addressVariables,
    skip: addressSkip,
  });

  const { edges, total } = addressData?.addresses ?? {
    edges: [],
    total: 0,
  };
  const addresses = edges.map((item) => item.node);
  const shipAddresses = addresses.filter((address) => address.isShipping === 1);
  let defaultShipAddressToDisplay = shipAddresses ? shipAddresses[0] : null;
  //console.log("compandyid: " + (companyId ? companyId : ""));

  //console.log("before defaultShipAddressToDisplay: " + (defaultShipAddressToDisplay ? JSON.stringify(defaultShipAddressToDisplay) : ""));

  //console.log("in accounts: " + (authCtx?.customerPrimaryAddresses ? JSON.stringify(authCtx.customerPrimaryAddresses) : ""));

  if(companyId && authCtx?.customerPrimaryAddresses && authCtx?.customerPrimaryAddresses[''+companyId]?.defaultShipId){
    //console.log("shipto: " + authCtx?.customerPrimaryAddresses[''+companyId]?.defaultShipId)
    defaultShipAddressToDisplay = shipAddresses.find((shipAddr) => shipAddr.entityId === ''+(authCtx?.customerPrimaryAddresses[''+companyId].defaultShipId));
    if(!defaultShipAddressToDisplay){
      defaultShipAddressToDisplay = shipAddresses ? shipAddresses[0] : null;
    }
  }

  //console.log("after defaultShipAddressToDisplay: " + (defaultShipAddressToDisplay ? JSON.stringify(defaultShipAddressToDisplay) : ""));

  const shipAddress = selectedShipAddress
    ? selectedShipAddress
    : defaultShipAddressToDisplay
    ? defaultShipAddressToDisplay
    : null;

  const accountChangeHandler = (soldTo, shipToAddress, companyName, salesOrg, fullSAPAccountNumber) => {
    hideShipAccountModalHandler();
    setSelectedShipAddress(shipToAddress);
    const accountInfo = {
      soldTo: soldTo,
      shipTo: shipToAddress.label,
      companyName: companyName,
      fullAct: fullSAPAccountNumber,
    };
    //props.accountChangeHandler(accountInfo);
  };

  const [
    beginMasquerade,
    {
      data: superAdminBeginMasqueradeData,
      loading: superAdminBeginMasqueradeLoading,
      error: superAdminBeginMasqueradeError,
    },
  ] = useMutation(SUPER_ADMIN_BEGIN_MASQUERADE, {
    refetchQueries: [
      {
        query: SUPER_ADMIN_MASQUERADING,
        variables: superAdminMasqueradingVariables,
        awaitRefetchQueries: true,
      },
      //{
      // query: B3_GET_ALL_ADDRESSES,
      // variables: { companyId: companyId },
      //},
    ],
  });

  const companyChangeHandler = (event) => {
    setIsLoading(true);
    const companyIdInt = parseInt(event.target.value);
    setSelectedShipAddress(null);

    const beginMasqueradeVariables = {
      companyId: companyIdInt,
      userId: parseInt(authCtx.bundleAuth.authorization.result.user.id),
    };
    beginMasquerade({
      variables: beginMasqueradeVariables,
    });

    //addressRefetch({companyId: companyIdInt})

    setIsLoading(false);
  };
  // ***********************            GraphQL End            *********************************

  useEffect(() => {
    if (
      acctNumber &&
      shipAddress &&
      companyName
    ) {
      const accountInfo = {
        soldTo: acctNumber,
        shipTo: shipAddress.label ? shipAddress.label : "",
        companyName: companyName,
        shipAddress: shipAddress,
        fullAct: fullSAPAccountNumber,
      };
      const webRef =
        props.quote && props.quote.webReference ? props.quote.webReference : "";
      props.accountChangeHandler(accountInfo, webRef);
    }
  }, [acctNumber, shipAddress, companyName]);

  let label_act = "Account";
  let label_login = "Login";
  let label_viewActInfo = "to view account information";
  let lable_delivery_addr = "Delivery Address";
  let label_select_alt_addr = "Select an alternate address";
  let label_link_acct_link = "Link your Bio-Rad";
  let label_link_acct_text = "account to view your pricing.";

  if (appCtx && appCtx.bioradResources) {
    label_act = appCtx.bioradResources.Account
      ? appCtx.bioradResources.Account
      : "Account";
    label_login = appCtx.bioradResources.Login
      ? appCtx.bioradResources.Login
      : "Login";
    label_viewActInfo = appCtx.bioradResources.View_Act_Info
      ? appCtx.bioradResources.View_Act_Info
      : "to view account information";
    lable_delivery_addr = appCtx.bioradResources.Delivery_Add
      ? appCtx.bioradResources.Delivery_Add
      : "Delivery Address";
    label_select_alt_addr = appCtx.bioradResources
      .Alternate_Delivery_Address_Widget
      ? appCtx.bioradResources.Alternate_Delivery_Address_Widget
      : "Select an alternate address";
    label_link_acct_link = appCtx.bioradResources.Link_Account_Link
      ? appCtx.bioradResources.Link_Account_Link
      : "Link your Bio-Rad";
    label_link_acct_text = appCtx.bioradResources.Link_Account_Text
      ? appCtx.bioradResources.Link_Account_Text
      : "account to view your pricing.";
  }

  const webRef =
    props.quote && props.quote.webReference
      ? "/" + props.quote.webReference
      : "";
  const loginUrl =
    appCtx.envProp.loginUrl +
    appCtx.envProp.appServer +
    appCtx.envProp.appName +
    webRef +
    "&ui_locales=" +
    appCtx.lang;
  if (!appCtx.envProp.appServer) return null;
    const linkAcctUrl =
      appCtx.envProp.appServer +
      "bc/" +
      appCtx.lang.toLowerCase() +
      "-" +
      appCtx.country.toLowerCase() +
      "/join-company?redirect=/quote-app";
      
  return (
    <Card className={`${props?.rebrand ? styles.newAccount:''} ${styles.account}`}>
      <div>
        <span className={props?.rebrand ? styles.newTitle:styles.headertitle}>{label_act}</span>
      </div>
      {!authCtx.isLoggedIn &&
        !(
          isLoading ||
          superAdminMasqueradingLoading ||
          superAdminCompaniesLoading ||
          companyLoading ||
          addressLoading
        ) && (
          <div className={styles.btnUnderline}>
            <p>
              <a href={loginUrl} className={styles.btnlinkU}>{label_login}</a>
              &nbsp;<span>{label_viewActInfo}</span>
            </p>
          </div>
        )}
        {authCtx.isLoggedIn && (superAdminMasqueradingError?.graphQLErrors || superAdminMasqueradingError?.networkError) && (
          <div>
          {superAdminMasqueradingError?.networkError && (
            <div>
              <span>{superAdminMasqueradingError.networkError.message}</span>
              {superAdminMasqueradingError?.networkError?.statusCode && (
                <span hidden>{superAdminMasqueradingError.networkError.statusCode}</span>
              )}
            </div>
          )}
          {superAdminMasqueradingError?.graphQLErrors && superAdminMasqueradingError.graphQLErrors.map(({ message }, i) => (
            <span key={i}>{message}</span>
          ))}
        </div>
        )}
        {authCtx.isLoggedIn && (superAdminCompaniesError?.graphQLErrors || superAdminCompaniesError?.networkError) && (
          <div>
          {superAdminCompaniesError?.networkError && (
            <div>
              <span>{superAdminCompaniesError.networkError.message}</span>
              {superAdminCompaniesError?.networkError?.statusCode && (
                <span hidden>{superAdminCompaniesError.networkError.statusCode}</span>
              )}
            </div>
          )}
          {superAdminCompaniesError?.graphQLErrors && superAdminCompaniesError.graphQLErrors.map(({ message }, i) => (
            <span key={i}>{message}</span>
          ))}
        </div>
        )}
        {authCtx.isLoggedIn && (companyError?.graphQLErrors || companyError?.networkError) && (
          <div>
          {companyError?.networkError && (
            <div>
              <span>{companyError.networkError.message}</span>
              {companyError?.networkError?.statusCode && (
                <span hidden>{companyError.networkError.statusCode}</span>
              )}
            </div>
          )}
          {companyError?.graphQLErrors && companyError.graphQLErrors.map(({ message }, i) => (
            <span key={i}>{message}</span>
          ))}
        </div>
        )}
        {authCtx.isLoggedIn && (addressError?.graphQLErrors || addressError?.networkError) && (
          <div>
            {addressError?.networkError && (
              <div>
                <span>{addressError.networkError.message}</span>
                {addressError?.networkError?.statusCode && (
                  <span hidden>{addressError.networkError.statusCode}</span>
                )}
              </div>
            )}
            {addressError?.graphQLErrors && addressError.graphQLErrors.map(({ message }, i) => (
              <span key={i}>{message}</span>
            ))}
          </div>
        )}
      
        {authCtx.isLoggedIn && !isLoading && !adminCompanies && !acctNumber && accountEnabled && (
          <div className={styles.btnUnderline}>
            <p>
              <a href={linkAcctUrl} className={styles.btnlinkU}>{label_link_acct_link}</a>
              &nbsp;<span>{label_link_acct_text}</span>
            </p>
          </div>
        )}
      
      {authCtx.isLoggedIn && !adminCompanies && acctNumber && (
        <div>
          <span>{acctNumber}</span>
        </div>
      )}
      {authCtx.isLoggedIn && adminCompanies && (
        <div>
          <select onChange={companyChangeHandler}>{adminCompanies}</select>
        </div>
      )}
      {authCtx.isLoggedIn &&
        !(
          isLoading ||
          superAdminMasqueradingLoading ||
          superAdminCompaniesLoading ||
          companyLoading ||
          addressLoading
        ) &&
        shipAddress && (
          <div>
            <div>
              <span>
                {props?.rebrand ? (
                  <p className={styles.deliveryLabel}>{lable_delivery_addr}</p>
                ) : (
                  <strong>{lable_delivery_addr}</strong>
                )}
              </span>
            </div>
            {shipAddress.firstName && shipAddress.firstName.toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.firstName}</span>
              </div>
            )}
            {shipAddress.addressLine1 && shipAddress.addressLine1.toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.addressLine1}</span>
              </div>
            )}
            {shipAddress.city && shipAddress.city.toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.city}</span>
              </div>
            )}
            {shipAddress.state && shipAddress.state.toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.state}</span>
              </div>
            )}
            {shipAddress.zipCode && shipAddress.zipCode.toString().toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.zipCode}</span>
              </div>
            )}
            {shipAddress.countryCode && shipAddress.countryCode.toUpperCase() !== 'NULL' && (
              <div>
                <span>{shipAddress.countryCode}</span>
              </div>
            )}
            {shipAddresses.length > 1 && (
              <Button onClick={showShipAccountModalHandler}>
                {label_select_alt_addr}
              </Button>
            )}
          </div>
        )}
      {authCtx.isLoggedIn && isShipAccountModalDisplayed && (
        <ShipToAccounts
          acctNumber={acctNumber}
          shipAddresses={shipAddresses}
          onClose={hideShipAccountModalHandler}
          accountChangeHandler={accountChangeHandler}
          selectedShipToAddress={shipAddress.label}
          companyName={companyName}
          salesOrg={salesOrg}
          fullSAPAccountNumber={fullSAPAccountNumber}
        />
      )}
      {(isLoading ||
        superAdminMasqueradingLoading ||
        superAdminCompaniesLoading ||
        companyLoading ||
        addressLoading) && <p className={styles.loading_ring}></p>}
    </Card>
  );
};

export default Account;
